import React, { useEffect, useState } from 'react';
import { Separator } from '../Separator';
import { Icons } from '../Icons';
import { cn } from '../../lib/utils';

interface FavoriteToolButton {
  id: string;
  icon: string;
  label: string;
  tooltip?: string;
  commands: any;
  isActive: boolean;
  disabled: boolean;
  disabledText?: string;
}

interface FavoriteToolsIconsProps {
  servicesManager: any;
  side: 'left' | 'right';
}

const FavoriteToolsIcons: React.FC<FavoriteToolsIconsProps> = ({ servicesManager, side }) => {
  const { favoritesService, toolbarService, viewportGridService } = servicesManager.services;
  const [favoriteTools, setFavoriteTools] = useState<FavoriteToolButton[]>([]);

  const updateFavorites = () => {
    // Get favorite tool IDs for current modality
    const toolIds = favoritesService.getAvailableFavoriteTools();

    // Get current viewport for evaluation
    const viewportId = viewportGridService?.getActiveViewportId();

    // Map to tool button definitions with evaluation
    const tools = toolIds
      .map(id => {
        const button = toolbarService.getButton(id);
        if (!button) return null;

        // Evaluate button state (active, disabled, etc.)
        let evaluateResult = { disabled: false, isActive: false, className: '', disabledText: '' };

        if (button.props.evaluate) {
          const evaluateFn = typeof button.props.evaluate === 'string'
            ? toolbarService._evaluateFunction?.[button.props.evaluate]
            : button.props.evaluate;

          if (typeof evaluateFn === 'function') {
            evaluateResult = {
              ...evaluateResult,
              ...(evaluateFn({
                viewportId,
                button,
                disabledText: button.props.tooltip
              }) || {})
            };
          }
        }

        return {
          id: button.id,
          icon: button.props.icon || 'MissingIcon',
          label: button.props.label || button.id,
          tooltip: button.props.tooltip,
          commands: button.props.commands,
          isActive: evaluateResult.isActive || false,
          disabled: evaluateResult.disabled || false,
          disabledText: evaluateResult.disabledText,
        };
      })
      .filter(Boolean) as FavoriteToolButton[];

    setFavoriteTools(tools);
  };

  useEffect(() => {
    // Initial load
    updateFavorites();

    // Subscribe to favorites updates
    const favoritesUnsub = favoritesService.subscribe(
      favoritesService.events.FAVORITES_UPDATED,
      updateFavorites
    );

    // Subscribe to modality changes
    const modalityUnsub = favoritesService.subscribe(
      favoritesService.events.MODALITY_CHANGED,
      updateFavorites
    );

    // Subscribe to toolbar changes (for active state updates and button modifications)
    const toolbarStateUnsub = toolbarService.subscribe(
      toolbarService.EVENTS.TOOL_BAR_STATE_MODIFIED,
      updateFavorites
    );

    const toolbarModifiedUnsub = toolbarService.subscribe(
      toolbarService.EVENTS.TOOL_BAR_MODIFIED,
      updateFavorites
    );

    // Subscribe to viewport changes (for active state updates)
    const viewportUnsub = viewportGridService?.subscribe(
      viewportGridService.EVENTS.ACTIVE_VIEWPORT_ID_CHANGED,
      updateFavorites
    );

    return () => {
      favoritesUnsub?.unsubscribe();
      modalityUnsub?.unsubscribe();
      toolbarStateUnsub?.unsubscribe();
      toolbarModifiedUnsub?.unsubscribe();
      viewportUnsub?.unsubscribe();
    };
  }, [favoritesService, toolbarService, viewportGridService]);

  const handleToolClick = (toolId: string, commands: any) => {
    const viewportId = viewportGridService?.getActiveViewportId();

    // Record interaction via toolbar service (same as header toolbar)
    toolbarService.recordInteraction(
      {
        itemId: toolId,
        commands,
        viewportId,
      },
      {
        refreshProps: { viewportId },
      }
    );
  };

  if (!favoriteTools.length) {
    return null;
  }

  return (
    <>
      <Separator className="my-2 bg-gray-700" />
      <div className="mt-3 flex flex-col items-center rounded-lg bg-[hsl(var(--toolbar-bg))] pt-[0.75rem] pb-[0.25rem] px-[0.2rem]">
        {/* Star icon - at top */}
        <span className="flex items-center justify-center mb-[0.4rem]">
          <Icons.Star className="h-[1rem] w-[1rem] text-yellow-400" />
        </span>

        {/* Tool buttons - vertical list with gap */}
        <div className="flex flex-col gap-[0.25rem]">
          {favoriteTools.map(tool => (
            <button
              key={tool.id}
              onClick={() => !tool.disabled && handleToolClick(tool.id, tool.commands)}
              disabled={tool.disabled}
              title={tool.tooltip}
              data-cy={tool.id}
              data-tool={tool.id}
              data-active={tool.isActive}
              className={cn(
                'flex h-[1.75rem] w-[1.75rem] items-center justify-center rounded-lg p-1 transition-colors',
                tool.isActive
                  ? 'bg-highlight text-foreground hover:!bg-highlight/80'
                  : 'bg-[hsl(var(--popover))] text-foreground',
                tool.disabled && 'opacity-40 cursor-not-allowed'
              )}
            >
              <Icons.ByName
                name={tool.icon}
                className="h-7 w-7"
              />
            </button>
          ))}
        </div>
      </div>
    </>
  );
};

export default FavoriteToolsIcons;
